import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { authErrorMessage, normalizeEmail, register as registerUser } from "../service/authService";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
} from "@mui/material";
import {
  Email,
  Lock,
  Person,
  Home as HomeIcon,
  Badge as BadgeIcon,
  Phone as PhoneIcon,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";

const SignUpPage = () => {
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();

  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const goToLoginSection = () => navigate("/login");

  const onSubmit = async (data) => {
    setSubmitError("");

    if (data.password !== data.confirmPassword) {
      setSubmitError("Passwords do not match.");
      return;
    }

    const payload = {
      username: normalizeEmail(data.username),
      password: data.password,
      firstname: data.firstname,
      lastname: data.lastname,
      address: data.address,
      taxId: data.taxId,
      phone: data.phone,
    };

    setLoading(true);
    try {
      await registerUser(payload);
      navigate("/login", { state: { registered: true } });
    } catch (error) {
      setSubmitError(authErrorMessage(error, "register"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ width: "100%", maxWidth: 480 }}>
      <Card
        sx={{
          borderRadius: 4,
          overflow: "hidden",
          boxShadow: "0 20px 50px rgba(0,0,0,0.18)",
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
          backgroundColor: "rgba(255,255,255,0.86)",
          border: "1px solid rgba(0,0,0,0.06)",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 3,
            py: 2.5,
            background:
              "linear-gradient(135deg, rgba(16,185,129,0.10), rgba(37,99,235,0.08))",
            borderBottom: "1px solid rgba(0,0,0,0.06)",
          }}
        >
          <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.1 }}>
            Create your account
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.7, color: "text.secondary" }}>
            Join Rustic World and start shopping today.
          </Typography>
        </Box>

        <CardContent sx={{ px: 3, py: 3 }}>
          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          )}


          <form onSubmit={handleSubmit(onSubmit)}>
            <TextField
              label="Email"
              type="email"
              fullWidth
              margin="normal"
              autoComplete="email"
              inputMode="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              {...register("username", {
                required: "Email is required",
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: "Invalid email address",
                },
              })}
              error={!!errors.username}
              helperText={errors.username?.message}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label="Password"
              type={showPass ? "text" : "password"}
              fullWidth
              margin="normal"
              autoComplete="new-password"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              {...register("password", {
                required: "Password is required",
                minLength: { value: 6, message: "Minimum 6 characters" },
              })}
              error={!!errors.password}
              helperText={errors.password?.message}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPass((s) => !s)}
                      edge="end"
                      size="small"
                      aria-label="toggle password visibility"
                    >
                      {showPass ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label="Confirm Password"
              type={showConfirm ? "text" : "password"}
              fullWidth
              margin="normal"
              autoComplete="new-password"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              {...register("confirmPassword", {
                required: "Please confirm your password",
                validate: (value) =>
                  value === watch("password") || "Passwords do not match",
              })}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword?.message}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirm((s) => !s)}
                      edge="end"
                      size="small"
                      aria-label="toggle confirm password visibility"
                    >
                      {showConfirm ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <TextField
                label="First Name"
                fullWidth
                margin="normal"
                {...register("firstname", {
                  required: "First name is required",
                })}
                error={!!errors.firstname}
                helperText={errors.firstname?.message}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="Last Name"
                fullWidth
                margin="normal"
                {...register("lastname", {
                  required: "Last name is required",
                })}
                error={!!errors.lastname}
                helperText={errors.lastname?.message}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <TextField
              label="Address"
              fullWidth
              margin="normal"
              {...register("address", { required: "Address is required" })}
              error={!!errors.address}
              helperText={errors.address?.message}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <HomeIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <TextField
                label="Tax ID (optional)"
                fullWidth
                margin="normal"
                {...register("taxId")}
                error={!!errors.taxId}
                helperText={
                  errors.taxId?.message || "Leave empty if not applicable"
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BadgeIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="Phone"
                fullWidth
                margin="normal"
                {...register("phone", { required: "Phone number is required" })}
                error={!!errors.phone}
                helperText={errors.phone?.message}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              sx={{
                mt: 2,
                py: 1.2,
                borderRadius: 2,
                fontWeight: 800,
                textTransform: "none",
              }}
            >
              {loading ? "Creating account..." : "Sign Up"}
            </Button>
          </form>

          <Typography variant="body2" align="center" sx={{ mt: 2 }}>
            You already have an account?{" "}
            <Box
              component="button"
              type="button"
              onClick={goToLoginSection}
              sx={{
                cursor: "pointer",
                border: 0,
                background: "transparent",
                p: 0,
                font: "inherit",
                color: "primary.main",
                fontWeight: 700,
                "&:hover": { textDecoration: "underline" },
              }}
            >
              Log in here
            </Box>
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SignUpPage;
